import { useMemo, useState, useCallback } from 'react';
import { t, Trans } from '@lingui/macro';

import Search from '@decisiv/iconix/lib/components/Search';

import SearchInput from '@decisiv/ui-components/lib/components/SearchInput';
import { P } from '@decisiv/ui-components/lib/components/Typography';
import Loading from '@decisiv/ui-components/lib/components/Loading';

import useSort from 'utils/useSort';

import { useCurrentUserContext } from 'utils/User';
import * as ManagementLayout from 'components/ManagementLayout';
import PaginationFooter from 'components/PaginationFooter';
import SearchableFilter from 'components/SearchableFilter';
import EmptyResults from 'components/EmptyResults';
import useFooter from 'utils/useFooter';
import useDebouncedValue from 'hooks/useDebouncedValue';

import { SearchProvider } from 'contexts/SearchContext';
import ManagementTeamModalProvider from './CreateTeamModal/contexts/ManagementTeamModalContext';
import ManagementCreateTeamModal from './CreateTeamModal';
import { NoWrapHeaderTable } from '../components/styles';
import { useManagedSentinelTeamsQuery } from './hooks/useManagedSentinelTeamsQuery';

import { useManagementPageContext } from '../contexts/ManagementPageContext';
import useOemSegmentsFilters from '../OemSegments/hooks/useOemSegmentsFilters';
import useTeamsFilters from './hooks/useTeamsFilters';
import { generateColumns } from './columns';
import { ClearAllButton } from '../styles';
import { API_SEARCH_DEBOUNCE_DELAY_MS } from '../constants';

export type ManagedTeamsType = ExtractFromArray<
  ManagedSentinelTeamsQuery['managedSentinelTeams']['collection']
>;

const Teams = () => {
  const [searchText, setSearchTextValue, debouncedSearchText] =
    useDebouncedValue('', API_SEARCH_DEBOUNCE_DELAY_MS);

  const { footerRef } = useFooter();
  const { isSuperUser, isOemManager } = useCurrentUserContext();

  const {
    isFiltersDirty,
    pageParams,
    handleChangeOemSegmentsFilter,
    handleChangeTeamsFilter,
    handleClearFilters,
  } = useManagementPageContext();

  const [page, setPage] = useState(1);

  const [{ sort }, { getSortProps }] = useSort();
  const { defaultSort, onSort } = getSortProps();

  const { data, loading } = useManagedSentinelTeamsQuery({
    variables: {
      sort,
      page,
      ids: pageParams.teamIds,
      search: debouncedSearchText.length > 2 ? debouncedSearchText : undefined,
      oemSegmentIds: pageParams.oemSegmentIds,
    },
  });

  const oemSegmentsFilterProps = useOemSegmentsFilters({
    value: pageParams.oemSegmentIds,
    onSubmit: handleChangeOemSegmentsFilter,
  });

  const teamFilterProps = useTeamsFilters({
    value: pageParams.teamIds,
    oemSegmentIds: pageParams.oemSegmentIds,
    onSubmit: handleChangeTeamsFilter,
  });

  const handleSearchTextChange = useCallback(
    ({ target: { value } }) => {
      setSearchTextValue(value);
      setPage(1);
    },
    [setSearchTextValue, setPage],
  );

  const columns = useMemo(() => generateColumns({ onSort }), [onSort]);

  const teams = data?.managedSentinelTeams?.collection ?? [];
  const pagination = data?.managedSentinelTeams?.pagination;

  return (
    <ManagementLayout.TabBody.Container>
      <ManagementLayout.TabBody.Header.Container>
        <ManagementLayout.TabBody.Header.Left>
          {(isSuperUser || isOemManager) && (
            <ManagementTeamModalProvider>
              <ManagementCreateTeamModal />
            </ManagementTeamModalProvider>
          )}
        </ManagementLayout.TabBody.Header.Left>
        <ManagementLayout.TabBody.Header.Right>
          <P size="small" marginRight={1}>
            <Trans>
              Search Teams by Name, Owner email, Portal ID or Team Key:
            </Trans>
          </P>
          <SearchInput
            hideLabel
            aria-label={t`Search Teams by Name, Owner email, Portal ID or Team Key`}
            size="small"
            name="searchByTeam"
            value={searchText}
            onChange={handleSearchTextChange}
          />
        </ManagementLayout.TabBody.Header.Right>
      </ManagementLayout.TabBody.Header.Container>
      <ManagementLayout.TabBody.Content.Container>
        <ManagementLayout.TabBody.Content.Filters>
          <SearchableFilter
            popoverLabel={t`OEM Segments`}
            placement="bottom-start"
            {...oemSegmentsFilterProps}
          />
          <SearchableFilter popoverLabel={t`Teams`} {...teamFilterProps} />
          {isFiltersDirty && (
            <ClearAllButton
              variant="ghost"
              onClick={handleClearFilters}
              text={t`Clear All`}
              size="small"
            />
          )}
        </ManagementLayout.TabBody.Content.Filters>
        {loading && (
          <Loading
            flex={1}
            justifyContent="center"
            size="large"
            title={t`Loading...`}
          />
        )}
        {!loading && teams.length === 0 && (
          <EmptyResults
            title={t`No Teams Found`}
            subtitle={t`No Managed Teams Found`}
            icon={Search}
          />
        )}
        {!loading && teams.length > 0 && (
          <SearchProvider searchString={debouncedSearchText}>
            <NoWrapHeaderTable
              height="100%"
              data={teams}
              scroll={['x', 'y']}
              columns={columns}
              onSort={onSort}
              defaultSort={defaultSort}
              footer={() => (
                <PaginationFooter
                  ref={footerRef}
                  // @ts-expect-error - JS file
                  title={t`Teams`}
                  setCurrentPage={setPage}
                  totalOnPage={teams?.length}
                  page={pagination?.page}
                  perPage={pagination?.perPage}
                  totalEntries={pagination?.totalEntries}
                  totalPages={pagination?.totalPages}
                />
              )}
            />
          </SearchProvider>
        )}
      </ManagementLayout.TabBody.Content.Container>
    </ManagementLayout.TabBody.Container>
  );
};

export default Teams;
